/**
 * Atlas — Property transfer inbox.
 *
 * Lists all transfer requests where I'm either initiator or recipient.
 * Two action buttons per row when I'm the recipient on a pending row
 * (Accept / Decline); single Cancel button when I'm the initiator on a
 * pending row.
 *
 * Reached via a notification link, or via /transfers directly.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { transfersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_META = {
  pending:   { label: 'Pending',   bg: 'rgba(160,96,0,0.12)', color: '#a06000' },
  accepted:  { label: 'Accepted',  bg: 'rgba(31,58,46,0.12)', color: 'var(--forest)' },
  declined:  { label: 'Declined',  bg: 'rgba(183,58,58,0.12)', color: 'var(--crimson)' },
  cancelled: { label: 'Cancelled', bg: 'rgba(0,0,0,0.08)',    color: 'var(--ink-4)' },
  expired:   { label: 'Expired',   bg: 'rgba(0,0,0,0.08)',    color: 'var(--ink-4)' },
};

function fmtRelative(iso) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export default function TransfersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // id currently mutating

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await transfersAPI.list();
      setItems(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load transfers.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (req, action) => {
    setBusy(req.id);
    try {
      if      (action === 'accept')  await transfersAPI.accept(req.id);
      else if (action === 'decline') await transfersAPI.decline(req.id);
      else if (action === 'cancel')  await transfersAPI.cancel(req.id);
      await load();
      if (action === 'accept') navigate(`/properties/${req.property_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Action failed.');
    } finally { setBusy(null); }
  };

  return (
    <div className="page-wrap" style={{ maxWidth: 800 }}>
      <div className="page-header" style={{ marginBottom: 22 }}>
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            Inbox
          </div>
          <h1 className="serif" style={{ fontSize: 28, margin: '4px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
            Ownership transfers
          </h1>
        </div>
      </div>

      {loading ? (
        <div style={{ color: 'var(--ink-4)', textAlign: 'center', padding: 60 }}>Loading…</div>
      ) : error ? (
        <div style={{ background: '#fdecea', border: '1px solid #f5c6c6', borderRadius: 8, padding: '12px 16px', color: '#b71c1c', fontSize: 13 }}>{error}</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--ink-4)' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🤝</div>
          <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)' }}>
            No transfer requests yet
          </div>
          <p style={{ fontSize: 13, maxWidth: 360, margin: '8px auto 0' }}>
            Outgoing transfers and any incoming requests for properties
            handed to you will appear here.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((req) => {
            const meta = STATUS_META[req.status] || STATUS_META.pending;
            const isRecipient = user?.id === req.to_user_id;
            const isInitiator = user?.id === req.from_user_id;
            const includedSections = [
              ['Timeline',    req.include_timeline],
              ['Financials',  req.include_financials],
              ['Systems',     req.include_systems],
              ['Documents',   req.include_documents],
              ['Photos',      req.include_photos],
              ['Inspections', req.include_inspections],
            ];
            return (
              <div key={req.id} style={{
                background: 'var(--card)', border: '1px solid var(--hairline)',
                borderRadius: 12, padding: 18,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="serif" style={{ fontSize: 16, fontWeight: 600 }}>
                      {req.property_address || 'Property'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
                      {isRecipient
                        ? <>From <strong>{req.from_user_name}</strong> · {fmtRelative(req.created_at)}</>
                        : <>To <strong>{req.to_user_name}</strong> · {fmtRelative(req.created_at)}</>}
                    </div>
                  </div>
                  <span style={{
                    padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    background: meta.bg, color: meta.color,
                    textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
                  }}>{meta.label}</span>
                </div>

                {/* Included slices */}
                <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 10 }}>
                  Includes: {includedSections.filter(([, v]) => v).map(([k]) => k).join(', ') || '— address only'}
                </div>

                {req.note && (
                  <div style={{ fontSize: 13, color: 'var(--ink-2)', background: 'var(--paper-2)', padding: '8px 12px', borderRadius: 6, marginBottom: 10 }}>
                    "{req.note}"
                  </div>
                )}

                {req.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {isRecipient && (
                      <>
                        <button className="btn ghost sm" disabled={busy === req.id} onClick={() => act(req, 'decline')}>
                          Decline
                        </button>
                        <button className="btn primary sm" disabled={busy === req.id} onClick={() => act(req, 'accept')}>
                          {busy === req.id ? '…' : 'Accept transfer'}
                        </button>
                      </>
                    )}
                    {isInitiator && (
                      <button className="btn ghost sm" disabled={busy === req.id} onClick={() => act(req, 'cancel')}>
                        {busy === req.id ? '…' : 'Cancel request'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
